import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConnectionStatusModal } from '../connections/ConnectionStatusModal';
import { Avatar } from './Avatar';
import { ConnectionLevelBadge } from './ConnectionLevelBadge';

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';

/** The minimal shape any user summary needs to render attribution. */
export interface UserAttributionSummary {
  id?: string;
  displayName?: string | null;
  businessName?: string | null;
  avatarUrl?: string | null;
  /** 1 = direct, 2 = friend-of-friend, 3/null = anyone (no badge). */
  connectionLevel?: number | null;
}

interface UserAttributionProps {
  summary?: UserAttributionSummary | null;
  /** Avatar size. Default 'xs' (inline attribution). */
  size?: AvatarSize;
  /** Optional leading word, e.g. "by" → "by Jane". */
  prefix?: string;
  /** Shown when the summary has no name. */
  fallbackName?: string;
  /** Wrapper classes (e.g. text color for the name row). */
  className?: string;
  /** Extra classes on the name text. */
  nameClassName?: string;
  /**
   * Opt-in: make the row a button that opens the connection-status panel
   * (status + one-tap "Send connection request"). No-op unless the summary
   * carries an `id`. Off by default so existing surfaces are unchanged.
   */
  interactive?: boolean;
  /**
   * Render as an inline-flex run (avatar → name → badge) that flows inside
   * surrounding text — e.g. embedded mid-sentence in a toast. A span root
   * instead of the block row. Not compatible with `interactive`.
   */
  inline?: boolean;
}

// Per-size badge typography + anchor now lives inside ConnectionLevelBadge —
// see `SIZE_MAP` there. UserAttribution just passes its `size` through; do
// NOT push position overrides down to call sites (that's the drift this
// component exists to kill).

/**
 * THE canonical way to show another user: avatar + name + connection-level
 * badge. Use this everywhere a non-self person is attributed (dish "by chef",
 * order counterparty, activity actor) so the three always travel together and
 * never drift. The badge renders only for levels 1/2 (see ConnectionLevelBadge)
 * and only when the summary carries `connectionLevel` — surfaces whose backend
 * doesn't compute it yet simply show avatar + name, identically shaped.
 *
 * Self displays (the signed-in user's own avatar in TopBar/menus) intentionally
 * do NOT use this — there's no connection level to yourself.
 */
export const UserAttribution: React.FC<UserAttributionProps> = ({
  summary,
  size = 'xs',
  prefix,
  fallbackName,
  className,
  nameClassName,
  interactive = false,
  inline = false,
}) => {
  const { t } = useTranslation('connections');
  const [statusOpen, setStatusOpen] = useState(false);

  const name =
    summary?.displayName || summary?.businessName || fallbackName || '';

  // Inline run for embedding mid-sentence (toasts): avatar → name → badge, no
  // block layout, no subtitle. A span root so it flows with surrounding text.
  if (inline) {
    return (
      <span
        className={['inline-flex items-center gap-1 align-middle', className ?? '']
          .filter(Boolean)
          .join(' ')}
      >
        <Avatar src={summary?.avatarUrl ?? null} name={name} size={size} />
        <span className={nameClassName}>{prefix ? `${prefix} ${name}` : name}</span>
        <ConnectionLevelBadge level={summary?.connectionLevel} />
      </span>
    );
  }

  const rowClass = ['flex items-center gap-1.5', className ?? '']
    .filter(Boolean)
    .join(' ');

  const inner = (
    <>
      <Avatar src={summary?.avatarUrl ?? null} name={name} size={size} />
      {/* nameClassName goes on the flex row (not just the name span) so the badge
          emoji inherits the same font-size as the name — otherwise it falls back
          to the surrounding card's base size and reads as oversized. */}
      <span
        className={['flex items-center gap-1 min-w-0', nameClassName ?? '']
          .filter(Boolean)
          .join(' ')}
      >
        <span className="truncate">{prefix ? `${prefix} ${name}` : name}</span>
        <ConnectionLevelBadge level={summary?.connectionLevel} />
      </span>
    </>
  );

  // Opt-in tappable: only when asked AND we have an id to act on. The row lives
  // inside clickable cards (dish/order) that are themselves <button>s, so this
  // is a role="button" span (NOT a real <button>, which would be invalid nested
  // markup) and stops propagation so the tap doesn't also fire the card's
  // navigation.
  if (interactive && summary?.id) {
    const open = () => setStatusOpen(true);
    return (
      <>
        <span
          role="button"
          tabIndex={0}
          aria-label={t('status.openAria')}
          onClick={(e) => {
            e.stopPropagation();
            open();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              open();
            }
          }}
          className={[rowClass, 'cursor-pointer text-left hover:opacity-80 transition-opacity'].join(' ')}
        >
          {inner}
        </span>
        <ConnectionStatusModal
          summary={summary}
          isOpen={statusOpen}
          onClose={() => setStatusOpen(false)}
        />
      </>
    );
  }

  return <div className={rowClass}>{inner}</div>;
};
